import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { apiClient } from '../api';

export type Permissions = Record<string, boolean>;

interface PermissionsContextType {
  permissions: Permissions | null;
  loading: boolean;
  error: string | null;
  refetchPermissions: () => Promise<void>;
  hasPermission: (feature: string) => boolean;
}

const PermissionsContext = createContext<PermissionsContextType | undefined>(undefined);

// Default permissions (all restricted until loaded)
const defaultPermissions: Permissions = {
  'users.view': false,
  'users.edit': false,
  'users.approve': false,
  'users.delete': false,
  'gateways.view': false,
  'gateways.edit': false,
  'gateways.delete': false,
  'mediators.view': false,
  'mediators.edit': false,
  'mediators.delete': false,
  'mcp_proxies.view': false,
  'mcp_proxies.edit': false,
  'mcp_proxies.delete': false,
  'mpx_tenants.view': false,
  'mpx_tenants.edit': false,
  'mpx_tenants.delete': false,
  'trust_registries.view': false,
  'trust_registries.edit': false,
  'trust_registries.delete': false,
  'secrets.view': false,
  'secrets.edit': false,
  'secrets.delete': false,
  'notifications.view': false,
  'notifications.edit': false,
  'notifications.delete': false,
  'channels.view': false,
  'channels.edit': false,
  'channels.delete': false,
  'channels.capture': false,
  'pipes.view': false,
  'pipes.edit': false,
  'pipes.delete': false,
  'pipes.execute': false,
  'settings.view': false,
  'settings.edit': false,
  'metrics.view': false,
  'dashboard.view': false,
  'logs.view': false,
};

export function PermissionsProvider({ children }: { children: ReactNode }) {
  const [permissions, setPermissions] = useState<Permissions | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchPermissions = async () => {
    try {
      setLoading(true);
      setError(null);
      setPermissions(await apiClient.getPermissions());
    } catch (err) {
      console.error('Failed to fetch permissions:', err);
      setError('Failed to load permissions');
      // Set default restricted permissions on error
      setPermissions(defaultPermissions);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPermissions();
  }, []);

  const hasPermission = (feature: string): boolean => {
    if (!permissions) return false;
    return permissions[feature] === true;
  };

  const value: PermissionsContextType = {
    permissions,
    loading,
    error,
    refetchPermissions: fetchPermissions,
    hasPermission,
  };

  return (
    <PermissionsContext.Provider value={value}>
      {children}
    </PermissionsContext.Provider>
  );
}

export function usePermissions(): PermissionsContextType {
  const context = useContext(PermissionsContext);
  if (context === undefined) {
    throw new Error('usePermissions must be used within a PermissionsProvider');
  }
  return context;
}
